import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { academicApi, type DepartmentObjectiveDto } from '../../../lib/api/academic.api';
import { useTenantStore } from '../../../stores/tenant.store';
import { RepeatableListEditor, type RepeatableListItem } from './RepeatableListEditor';
import { errorText } from '../../../lib/error-text';

const DEPT_COLOR_SWATCHES = [
  '#7C3AED',
  '#0EA5E9',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#EC4899',
  '#6366F1',
  '#14B8A6',
  '#F97316',
];

export interface DepartmentRecord {
  id: string;
  name: string;
  code: string;
  status?: 'active' | 'inactive' | undefined;
  purpose?: string | undefined;
  learningObjectives?: DepartmentObjectiveDto[] | undefined;
  primaryColor?: string | undefined;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** When null → create mode. When set → edit existing department. */
  department: DepartmentRecord | null;
}

export function DepartmentEditDialog({ open, onClose, department }: Props): JSX.Element {
  const { currentTenantId } = useTenantStore();
  const editing = !!department;

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [purpose, setPurpose] = useState('');
  const [objectives, setObjectives] = useState<RepeatableListItem[]>([]);
  const [primaryColor, setPrimaryColor] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitting(false);
    if (department) {
      setName(department.name ?? '');
      setCode(department.code ?? '');
      setPurpose(department.purpose ?? '');
      setObjectives(
        (department.learningObjectives ?? []).map((o) => ({
          id: o.id,
          ...(o.code ? { code: o.code } : {}),
          text: o.text,
        })),
      );
      setPrimaryColor(department.primaryColor ?? '');
      setStatus(department.status ?? 'active');
    } else {
      setName('');
      setCode('');
      setPurpose('');
      setObjectives([]);
      setPrimaryColor('');
      setStatus('active');
    }
  }, [open, department]);

  const handleSubmit = async () => {
    if (!currentTenantId) return;
    if (!name.trim()) {
      setError('Nombre obligatorio');
      return;
    }
    if (!/^[A-Za-z0-9-]+$/.test(code)) {
      setError('Código debe ser alfanumérico (guiones permitidos)');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const cleanObjectives: DepartmentObjectiveDto[] = objectives
        .filter((o) => o.text.trim())
        .map((o) => ({
          id: o.id,
          ...(o.code && o.code.trim() ? { code: o.code.trim() } : {}),
          text: o.text.trim(),
        }));

      if (editing && department) {
        await academicApi.updateDepartment({
          tenantId: currentTenantId,
          departmentId: department.id,
          name: name.trim(),
          code: code.trim(),
          ...(purpose.trim() ? { purpose: purpose.trim() } : {}),
          learningObjectives: cleanObjectives,
          ...(primaryColor ? { primaryColor } : {}),
          status,
        });
      } else {
        await academicApi.createDepartment({
          tenantId: currentTenantId,
          name: name.trim(),
          code: code.trim(),
          ...(purpose.trim() ? { purpose: purpose.trim() } : {}),
          ...(cleanObjectives.length > 0 ? { learningObjectives: cleanObjectives } : {}),
          ...(primaryColor ? { primaryColor } : {}),
        });
      }
      onClose();
    } catch (e) {
      setError(errorText(e, 'Error guardando departamento'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? `Editar departamento` : 'Nuevo departamento'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              size="small"
              placeholder="Ej: Biblia"
            />
            <TextField
              label="Código"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              required
              size="small"
              placeholder="BI"
              sx={{ width: 140 }}
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
            {editing && (
              <TextField
                select
                label="Estado"
                value={status}
                onChange={(e) => setStatus(e.target.value as 'active' | 'inactive')}
                size="small"
                sx={{ width: 160 }}
              >
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
              </TextField>
            )}
          </Stack>

          <TextField
            label="Propósito del departamento"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            size="small"
            placeholder="Aparece en la sección 'Propósito del Departamento' de cada syllabus de un curso de este departamento."
          />

          <RepeatableListEditor
            label="Objetivos de aprendizaje del departamento (OADs)"
            helperText="Aparecen como lista en la sección 'Propósito del Departamento' del syllabus."
            textLabel="Objetivo"
            autoCodePrefix="OAD"
            items={objectives}
            onChange={setObjectives}
          />

          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
              Color de acento
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Usado en avatares y barras laterales. Se hereda al programa si éste no define el suyo.
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 1 }}
            >
              {DEPT_COLOR_SWATCHES.map((hex) => (
                <Tooltip key={hex} title={hex}>
                  <Box
                    role="button"
                    onClick={() => setPrimaryColor(hex)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: hex,
                      cursor: 'pointer',
                      border:
                        primaryColor.toLowerCase() === hex.toLowerCase()
                          ? '3px solid #fff'
                          : '2px solid transparent',
                      boxShadow:
                        primaryColor.toLowerCase() === hex.toLowerCase()
                          ? `0 0 0 2px ${hex}`
                          : 'inset 0 0 0 1px rgba(0,0,0,0.15)',
                      '&:hover': { transform: 'scale(1.1)' },
                      transition: 'transform 0.12s',
                    }}
                  />
                </Tooltip>
              ))}
              <TextField
                size="small"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                placeholder="#7C3AED"
                sx={{ width: 120 }}
                error={primaryColor !== '' && !/^#[0-9A-Fa-f]{6}$/.test(primaryColor)}
                inputProps={{ pattern: '^#[0-9A-Fa-f]{6}$', maxLength: 7 }}
              />
              {primaryColor && (
                <Button size="small" onClick={() => setPrimaryColor('')}>
                  Quitar
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !name.trim() || !code.trim()}
        >
          {submitting ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear departamento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
